import { computed } from 'vue';
import { interpret } from 'xstate';
import { useActor } from '@xstate/vue';

import { ErhlCustomerCurrentKey } from '@/helpers/const';
import { reopenPropertyDetailsIfMissingExisting } from '@/helpers/erhlCustomerPropertyDetails';
import { formatAddress } from '@/helpers/format';
import { deduplicateAddresses, hasAddressLine } from '@/helpers/useApplicationAddresses';
import {
  initMachine,
  loanApplicationMachineErhlCustomer,
  type ErhlCustomerLoanAppMachineContext,
} from '@/helpers/loanErhlCustomerAppMachine';
import {
  ErhlCustomerApplicationProcessSteps,
  ErhlCustomerProcessStep,
  ErhlCustomerProcessStepNames,
  StepStatus,
} from '@/types';

import type {
  Address,
  Applicant,
  ApplicantId,
  AssetId,
  ErhlCustomerApplicationProcessStepStatus,
  ErhlCustomerProcessStepEnum,
  LoanApplication,
  PropertyId,
} from '@/types';

/**
 * Reads the saved customer ERHL journey from localStorage, if any. Uses `ErhlCustomerCurrentKey`
 * (never the shared `CurrentKey`) so this journey only ever restores its own state.
 */
export const loadSavedState = (): ErhlCustomerLoanAppMachineContext | null => {
  const appKey = localStorage.getItem(ErhlCustomerCurrentKey) ?? '';
  const savedStateRaw = appKey ? localStorage.getItem(appKey) : null;
  if (!savedStateRaw) return null;
  try {
    return reopenPropertyDetailsIfMissingExisting(JSON.parse(savedStateRaw));
  } catch {
    // Corrupt saved state would otherwise crash module init; drop it and start fresh
    localStorage.removeItem(appKey);
    localStorage.removeItem(ErhlCustomerCurrentKey);
    return null;
  }
};

/** Storage key the journey's state is saved under, namespaced away from the other journeys */
export const savedStateKey = (loanApp: LoanApplication) =>
  `erhlCustomer-${loanApp.actionType}-${loanApp.applicationId}`;

const initialState: ErhlCustomerLoanAppMachineContext = loadSavedState() ?? initMachine();

const service = interpret(loanApplicationMachineErhlCustomer.withContext(initialState)).start();

service.onTransition((state) => {
  if (state.value !== 'gotoReady' && state.value !== 'start') {
    const currentStep = state.value as ErhlCustomerProcessStepEnum;
    if (state.context.status[currentStep] === StepStatus.Incomplete) {
      state.context.status[currentStep] = StepStatus.Ready;
    }

    state.context.current = currentStep;
    const loanApp = state.context.loanApp;
    if (loanApp.applicationId && loanApp.userType && currentStep !== ErhlCustomerProcessStep.Finished) {
      const key = savedStateKey(loanApp);
      localStorage.setItem(ErhlCustomerCurrentKey, key);
      localStorage.setItem(key, JSON.stringify(state.context));
    }
  }
});

const processSteps = Object.entries(ErhlCustomerApplicationProcessSteps).map(([step, info]) => ({
  id: step as ErhlCustomerProcessStepEnum,
  ...info,
}));

/**
 * Determines if a step should be visible in the sidebar. Every ERHL step applies to a customer
 * creating a new application, so only the Finished step is hidden.
 */
const shouldShowStep = (step: { id: ErhlCustomerProcessStepEnum; actions: string[] }, loanApp: LoanApplication) => {
  if (step.id === ErhlCustomerProcessStep.Finished) return false;
  return !!loanApp.userType && step.actions.includes(loanApp.actionType);
};

/**
 * Gets the current valid step and its order from the process steps
 * @param currentStepId - The current step ID from state
 * @returns Object containing validated step and its order
 */
const getCurrentStepInfo = (currentStepId: ErhlCustomerProcessStepEnum) => {
  const validStep = ErhlCustomerProcessStepNames.includes(currentStepId)
    ? currentStepId
    : ErhlCustomerProcessStepNames[0];
  return {
    step: validStep,
    order: ErhlCustomerApplicationProcessSteps[validStep].order,
  };
};

/**
 * Calculates the display status for a step based on its current status and position
 */
const calculateStepStatus = (
  step: { id: ErhlCustomerProcessStepEnum },
  stepStatus: StepStatus,
  currentStepOrder: number,
): StepStatus => {
  if (stepStatus === StepStatus.Incomplete && ErhlCustomerApplicationProcessSteps[step.id].order < currentStepOrder) {
    return StepStatus.Skipped;
  }
  return stepStatus;
};

export function useErhlCustomerLoanAppProcess() {
  const { state, send } = useActor(service);
  const currentStep = computed(() => state.value.context.current);
  const loanApp = computed(() => state.value.context.loanApp);
  const authorisedUser = computed(() => state.value.context.authorisedUser);

  const stepper = computed<ErhlCustomerApplicationProcessStepStatus[]>(() => {
    const { order: currentStepOrder } = getCurrentStepInfo(state.value.context.current);
    return processSteps.reduce<ErhlCustomerApplicationProcessStepStatus[]>((visibleSteps, step) => {
      if (!shouldShowStep(step, loanApp.value)) {
        return visibleSteps;
      }

      const stepStatus = state.value.context.status[step.id];
      const status = calculateStepStatus(step, stepStatus, currentStepOrder);

      if (status === StepStatus.Skipped) {
        return visibleSteps;
      }

      visibleSteps.push({ ...step, status });
      return visibleSteps;
    }, []);
  });

  const loanProperties = computed(() =>
    [
      ...loanApp.value.incomingProperties,
      ...loanApp.value.outgoingProperties,
      ...(loanApp.value?.supportingProperties ?? []),
    ].reduce((obj, item) => {
      obj[item.id] = formatAddress(item.address as Address);
      return obj;
    }, {} as Record<PropertyId | AssetId, string>),
  );

  /**
   * Addresses of the properties captured on this application's Property Details step.
   * Used to suggest addresses in the Borrower Details address fields.
   * `outgoingProperties` is excluded because ERHL always saves it empty.
   */
  const loanPropertyAddresses = computed<Address[]>(() =>
    deduplicateAddresses(
      [...(loanApp.value.incomingProperties ?? []), ...(loanApp.value.supportingProperties ?? [])]
        .map((property) => property.address as Address | undefined)
        .filter(hasAddressLine),
    ),
  );

  const loanApplicants = computed<Record<string, Applicant>>(() =>
    loanApp.value.applicants.reduce((obj, item) => {
      obj[item.id as string] = item;
      return obj;
    }, {} as Record<ApplicantId, Applicant>),
  );

  const saveAndGoNext = (updatedData: Partial<LoanApplication> = {}) => {
    if (currentStep.value === ErhlCustomerProcessStep.SubmitApplication) {
      const key = localStorage.getItem(ErhlCustomerCurrentKey);
      if (key) localStorage.removeItem(key);
      localStorage.setItem(ErhlCustomerCurrentKey, '');
    }
    send({ type: 'NEXT', loanApp: { ...state.value.context.loanApp, ...updatedData } });
  };

  const goBack = () => {
    send('BACK');
  };

  const restart = () => {
    send({ type: 'RESTART' });
  };

  return {
    state,
    service,
    stepper,
    currentStep,
    authorisedUser,
    loanApp,
    loanProperties,
    loanPropertyAddresses,
    loanApplicants,
    send,
    saveAndGoNext,
    goBack,
    restart,
  };
}
